/**
 * Requirements API functions.
 */

import apiClient from './client';
import type {
  Requirement,
  RequirementListResponse,
  RequirementOverviewResponse,
  RequirementComment,
  RequirementCommentCreate,
  RequirementAttachment,
  RequirementAttachmentCreate,
  RequirementUpdate,
  RequirementCreate,
  PackageRequirement,
  RequirementOperationLog,
  DesignDocument,
  DesignDocumentAttachment,
  DesignDocumentAttachmentCreate,
  DesignDocumentCreateUpdate,
  DesignDocumentListResponse,
  DesignDocumentVersion,
  DesignDocumentVersionDetail,
  DesignDocumentVersionUpdateRequest,
} from '@/types';

/**
 * Get requirements for a project, optionally filtered by subcategory.
 */
export const listRequirements = async (
  projectId: number,
  subcategory?: string
): Promise<RequirementListResponse> => {
  const params = subcategory ? { subcategory } : {};
  const response = await apiClient.get<RequirementListResponse>(
    `/requirements/${projectId}/`,
    { params }
  );
  return response.data;
};

/**
 * Get requirement overview (status distribution) for a project.
 */
export const getRequirementOverview = async (
  projectId: number
): Promise<RequirementOverviewResponse> => {
  const response = await apiClient.get<RequirementOverviewResponse>(
    `/requirements/${projectId}/overview/`
  );
  return response.data;
};

/**
 * Update a requirement (syncs to BVP).
 */
export const updateRequirement = async (
  projectId: number,
  requirementId: string,
  data: RequirementUpdate
): Promise<Requirement> => {
  const response = await apiClient.put<Requirement>(
    `/requirements/${projectId}/${requirementId}/`,
    data
  );
  return response.data;
};

/**
 * Update requirement status (via BVP dispatch/status).
 */
export const updateRequirementStatus = async (
  projectId: number,
  requirementId: string,
  data: { id: number; subcategory: string; status: string }
): Promise<{ status: string }> => {
  const response = await apiClient.put<{ status: string }>(
    `/requirements/${projectId}/${requirementId}/status/`,
    data
  );
  return response.data;
};

/**
 * Get comments for a requirement.
 */
export const listComments = async (
  projectId: number,
  requirementId: string
): Promise<RequirementComment[]> => {
  const response = await apiClient.get<RequirementComment[]>(
    `/requirements/${projectId}/${requirementId}/comments/`
  );
  return response.data;
};

/**
 * Create a comment for a requirement.
 */
export const createComment = async (
  projectId: number,
  requirementId: string,
  data: RequirementCommentCreate
): Promise<RequirementComment> => {
  const response = await apiClient.post<RequirementComment>(
    `/requirements/${projectId}/${requirementId}/comments/`,
    data
  );
  return response.data;
};

/**
 * Delete a comment.
 */
export const deleteComment = async (
  projectId: number,
  requirementId: string,
  commentId: number
): Promise<void> => {
  await apiClient.delete(
    `/requirements/${projectId}/${requirementId}/comments/${commentId}/`
  );
};

/**
 * Get attachments for a requirement.
 */
export const listAttachments = async (
  projectId: number,
  requirementId: string
): Promise<RequirementAttachment[]> => {
  const response = await apiClient.get<RequirementAttachment[]>(
    `/requirements/${projectId}/${requirementId}/attachments/`
  );
  return response.data;
};

/**
 * Create an attachment record (after OBS upload).
 */
export const createAttachment = async (
  projectId: number,
  requirementId: string,
  data: RequirementAttachmentCreate
): Promise<RequirementAttachment> => {
  const response = await apiClient.post<RequirementAttachment>(
    `/requirements/${projectId}/${requirementId}/attachments/`,
    data
  );
  return response.data;
};

/**
 * Delete an attachment.
 */
export const deleteAttachment = async (
  projectId: number,
  requirementId: string,
  attachmentId: number
): Promise<void> => {
  await apiClient.delete(
    `/requirements/${projectId}/${requirementId}/attachments/${attachmentId}/`
  );
};

/**
 * Get package-requirement associations.
 */
export const getPackageRequirements = async (
  packageId: number
): Promise<PackageRequirement[]> => {
  const response = await apiClient.get<PackageRequirement[]>(
    `/packages/${packageId}/requirements/`
  );
  return response.data;
};

/**
 * Update package-requirement associations (bulk replace).
 */
export const updatePackageRequirements = async (
  packageId: number,
  requirementIds: string[]
): Promise<PackageRequirement[]> => {
  const response = await apiClient.put<PackageRequirement[]>(
    `/packages/${packageId}/requirements/`,
    { requirement_ids: requirementIds }
  );
  return response.data;
};

/**
 * Get linked package for a requirement.
 */
export const getLinkedPackage = async (
  projectId: number,
  requirementId: string
): Promise<{ packageId: number | null; packageName: string | null }> => {
  const response = await apiClient.get<{ packageId: number | null; packageName: string | null }>(
    `/requirements/${projectId}/${requirementId}/linked-package/`
  );
  return response.data;
};

/**
 * Set linked package for a requirement (交付首版).
 */
export const setLinkedPackage = async (
  projectId: number,
  requirementId: string,
  packageId: number | null
): Promise<{ packageId: number | null; packageName: string | null }> => {
  const response = await apiClient.put<{ packageId: number | null; packageName: string | null }>(
    `/requirements/${projectId}/${requirementId}/linked-package/`,
    { packageId }
  );
  return response.data;
};

/**
 * Get all distinct TAG values used in a project.
 */
export const listProjectTags = async (projectId: number): Promise<string[]> => {
  const response = await apiClient.get<{ tags: string[] }>(
    `/requirements/${projectId}/tags/`
  );
  return response.data.tags;
};

/**
 * Get BSP tag for a requirement.
 */
export const getRequirementTag = async (
  projectId: number,
  requirementId: string
): Promise<{ id: number | null; value: string; updated_at: string | null }> => {
  const response = await apiClient.get(
    `/requirements/${projectId}/${requirementId}/tag/`
  );
  return response.data;
};

/**
 * Set/clear BSP tag for a requirement. Pass empty string to clear.
 */
export const updateRequirementTag = async (
  projectId: number,
  requirementId: string,
  value: string
): Promise<{ id: number | null; value: string; updated_at: string | null }> => {
  const response = await apiClient.put(
    `/requirements/${projectId}/${requirementId}/tag/`,
    { value }
  );
  return response.data;
};

export const listProjectTags1 = async (projectId: number): Promise<string[]> => {
  const response = await apiClient.get<{ tags: string[] }>(`/requirements/${projectId}/tags1/`);
  return response.data.tags;
};

export const updateRequirementTag1 = async (
  projectId: number,
  requirementId: string,
  value: string
): Promise<{ id: number | null; value: string; updated_at: string | null }> => {
  const response = await apiClient.put(
    `/requirements/${projectId}/${requirementId}/tag1/`,
    { value }
  );
  return response.data;
};

export const listProjectTags2 = async (projectId: number): Promise<string[]> => {
  const response = await apiClient.get<{ tags: string[] }>(`/requirements/${projectId}/tags2/`);
  return response.data.tags;
};

export const updateRequirementTag2 = async (
  projectId: number,
  requirementId: string,
  value: string
): Promise<{ id: number | null; value: string; updated_at: string | null }> => {
  const response = await apiClient.put(
    `/requirements/${projectId}/${requirementId}/tag2/`,
    { value }
  );
  return response.data;
};

/**
 * Get operation logs for a project's requirements.
 */
export const getRequirementOperationLogs = async (
  projectId: number,
  requirementId?: string
): Promise<RequirementOperationLog[]> => {
  const params = requirementId ? { requirement_id: requirementId } : {};
  const response = await apiClient.get<{ logs: RequirementOperationLog[] }>(
    `/requirements/${projectId}/operation-logs/`,
    { params }
  );
  return response.data.logs;
};

/**
 * Get delivery stages (交付阶段) for a project.
 * Feature: 002-requirement-delivery-stages
 *
 * Returns all unique delivery stage values from the project's requirements,
 * used to populate dropdown options for the deliveryStage field.
 */
export const fetchDeliveryStages = async (
  projectId: number
): Promise<{ delivery_stages: string[]; count: number; project_id: number }> => {
  const response = await apiClient.get<{ delivery_stages: string[]; count: number; project_id: number }>(
    `/requirements/${projectId}/delivery-stages/`
  );
  return response.data;
};

/**
 * Get 二级分类 (category2) values already used in a project.
 *
 * Returns all unique category2 values from the project's requirements,
 * used to populate dropdown suggestions for the category2 field.
 */
export const fetchCategory2Values = async (
  projectId: number
): Promise<{ category2_values: string[]; count: number; project_id: number }> => {
  const response = await apiClient.get<{ category2_values: string[]; count: number; project_id: number }>(
    `/requirements/${projectId}/category2-values/`
  );
  return response.data;
};

/**
 * Create a new requirement.
 */
export const createRequirement = async (
  projectId: number,
  data: RequirementCreate
): Promise<Requirement> => {
  const response = await apiClient.post<Requirement>(
    `/requirements/${projectId}/`,
    data
  );
  return response.data;
};

/**
 * Delete a requirement.
 */
export const deleteRequirement = async (
  projectId: number,
  requirementId: string
): Promise<{ deleted_from_projects?: string[] }> => {
  const response = await apiClient.delete<{ deleted_from_projects?: string[] }>(
    `/requirements/${projectId}/${requirementId}/`
  );
  return response.data || {};
};

/**
 * Map a delete-requirement error to a user-facing message.
 */
export const getRequirementDeleteErrorMessage = (error: unknown): string => {
  const err = error as { response?: { status?: number; data?: { message?: string } } };
  const status = err?.response?.status;
  if (status === 409) {
    return err.response?.data?.message || '该需求已关联底软包，请先取消关联后再删除';
  }
  if (status === 403) return '无权限删除该需求';
  if (status === 404) return err.response?.data?.message || '需求不存在或已被删除';
  return '删除失败，请稍后重试';
};

export interface BatchDeleteResult {
  deletedIds: string[];
  failed: { requirementId: string; message: string }[];
  deletedFromProjects: string[];
}

/**
 * Delete requirements one by one, collecting per-item results.
 * Sequential to reuse the existing single-delete permission/conflict checks.
 */
export const deleteRequirements = async (
  projectId: number,
  requirementIds: string[]
): Promise<BatchDeleteResult> => {
  const deletedIds: string[] = [];
  const failed: BatchDeleteResult['failed'] = [];
  const deletedFromProjects = new Set<string>();

  for (const requirementId of requirementIds) {
    try {
      const result = await deleteRequirement(projectId, requirementId);
      deletedIds.push(requirementId);
      result.deleted_from_projects?.forEach((p) => deletedFromProjects.add(p));
    } catch (error) {
      failed.push({
        requirementId,
        message: getRequirementDeleteErrorMessage(error),
      });
    }
  }

  return { deletedIds, failed, deletedFromProjects: [...deletedFromProjects] };
};

/**
 * 获取项目的设计文档列表
 */
export const listDesignDocuments = async (
  projectId: number
): Promise<DesignDocumentListResponse> => {
  const response = await apiClient.get(
    `/requirements/${projectId}/design-documents/`
  );
  return response.data;
};

/**
 * 创建设计文档
 */
export const createDesignDocument = async (
  projectId: number,
  data: DesignDocumentCreateUpdate
): Promise<DesignDocument> => {
  const response = await apiClient.post(
    `/requirements/${projectId}/design-documents/`,
    data
  );
  return response.data;
};

/**
 * 更新设计文档
 */
export const updateDesignDocument = async (
  projectId: number,
  documentId: number,
  data: Partial<DesignDocumentCreateUpdate>
): Promise<DesignDocument> => {
  const response = await apiClient.put(
    `/requirements/${projectId}/design-documents/${documentId}/`,
    data
  );
  return response.data;
};

/**
 * 删除设计文档
 */
export const deleteDesignDocument = async (
  projectId: number,
  documentId: number
): Promise<void> => {
  await apiClient.delete(
    `/requirements/${projectId}/design-documents/${documentId}/`
  );
};

/**
 * 获取设计文档附件列表
 */
export const listDesignDocumentAttachments = async (
  projectId: number,
  documentId: number
): Promise<DesignDocumentAttachment[]> => {
  const response = await apiClient.get(
    `/requirements/${projectId}/design-documents/${documentId}/attachments/`
  );
  return response.data;
};

/**
 * 创建设计文档附件
 */
export const createDesignDocumentAttachment = async (
  projectId: number,
  documentId: number,
  data: DesignDocumentAttachmentCreate
): Promise<DesignDocumentAttachment> => {
  const response = await apiClient.post(
    `/requirements/${projectId}/design-documents/${documentId}/attachments/`,
    data
  );
  return response.data;
};

/**
 * 删除设计文档附件
 */
export const deleteDesignDocumentAttachment = async (
  projectId: number,
  documentId: number,
  attachmentId: number
): Promise<void> => {
  await apiClient.delete(
    `/requirements/${projectId}/design-documents/${documentId}/attachments/${attachmentId}/`
  );
};

/**
 * 获取设计文档版本历史
 */
export const getDesignDocumentVersions = async (
  projectId: number,
  documentId: number
): Promise<DesignDocumentVersion[]> => {
  const response = await apiClient.get(
    `/requirements/${projectId}/design-documents/${documentId}/versions/`
  );
  // Handle paginated response
  return response.data.results || response.data;
};

/**
 * 获取设计文档版本详情
 */
export const getDesignDocumentVersionDetail = async (
  projectId: number,
  documentId: number,
  versionId: number
): Promise<DesignDocumentVersionDetail> => {
  const response = await apiClient.get(
    `/requirements/${projectId}/design-documents/${documentId}/versions/${versionId}/`
  );
  return response.data;
};

/**
 * Get requirement source (需求来源).
 */
export const getRequirementSource = async (
  projectId: number,
  requirementId: string
): Promise<{ source_type: string; customer_req_ids: string[] }> => {
  const response = await apiClient.get<{ source_type: string; customer_req_ids: string[] }>(
    `/requirements/${projectId}/${requirementId}/source/`
  );
  return response.data;
};

/**
 * Update requirement source (需求来源).
 */
export const updateRequirementSource = async (
  projectId: number,
  requirementId: string,
  data: { source_type: string; customer_req_ids: string[] }
): Promise<{ source_type: string; customer_req_ids: string[] }> => {
  const response = await apiClient.put<{ source_type: string; customer_req_ids: string[] }>(
    `/requirements/${projectId}/${requirementId}/source/`,
    data
  );
  return response.data;
};

/**
 * 删除设计文档版本
 */
export const deleteDesignDocumentVersion = async (
  projectId: number,
  documentId: number,
  versionId: number
): Promise<void> => {
  await apiClient.delete(
    `/requirements/${projectId}/design-documents/${documentId}/versions/${versionId}/delete/`
  );
};

/**
 * 更新设计文档历史版本（编辑某个已发布版本的快照字段与附件）
 */
export const updateDesignDocumentVersion = async (
  projectId: number,
  documentId: number,
  versionId: number,
  data: DesignDocumentVersionUpdateRequest
): Promise<DesignDocumentVersionDetail> => {
  const response = await apiClient.put(
    `/requirements/${projectId}/design-documents/${documentId}/versions/${versionId}/`,
    data
  );
  return response.data;
};

/**
 * Send requirement change notification to configured recipients.
 */
export interface NotifyResponse {
  message: string;
  recipient_count: number;
  email_sent: boolean;
  notification_id: number;
}

export const sendRequirementChangeNotification = async (
  projectId: number,
  requirementIds: string[],
  password?: string,
  requirementsData?: Record<string, unknown>[]
): Promise<NotifyResponse> => {
  const response = await apiClient.post<NotifyResponse>(
    `/requirements/${projectId}/notify/`,
    { requirement_ids: requirementIds, password, requirements_data: requirementsData }
  );
  return response.data;
};

/** 一次批次里的一条需求：`project_id` 决定它属于哪个车型（详情链接、审计归属）。 */
export interface NotifyBatchItem {
  project_id: number;
  req_id: string;
  description?: string | null;
  owner?: string | null;
  delivery_stage?: string | null;
  attachment?: string | null;
  status?: string | null;
  priority?: string | null;
}

export interface NotifyBatchResponse {
  message: string;
  recipient_count: number;
  email_sent: boolean;
  project_count: number;
  requirement_count: number;
  notification_ids: number[];
}

/**
 * 跨车型批次发送：整个通知列表只发一封邮件，按车型分组展示。
 */
export const sendRequirementChangeNotificationBatch = async (
  items: NotifyBatchItem[],
  mountainName?: string | null,
  password?: string
): Promise<NotifyBatchResponse> => {
  const response = await apiClient.post<NotifyBatchResponse>(
    '/requirements/notify-batch/',
    { items, mountain_name: mountainName ?? null, password }
  );
  return response.data;
};

export const getDesignDocumentLogs = async (projectId: number) => {
  const response = await apiClient.get(`/requirements/${projectId}/design-documents/logs/`);
  return response.data;
};

export interface RequirementUser {
  username: string;
  displayName: string;
}

export const listUsers = async (search?: string): Promise<RequirementUser[]> => {
  const params = search ? { search } : {};
  const response = await apiClient.get<{ users: RequirementUser[] }>('/requirements/users/', { params });
  return response.data.users;
};

export const listMainlineProjects = async (): Promise<{ code: number; data: import('@/types').MainlineProject[] }> => {
  const response = await apiClient.get('/requirements/mainline-projects/');
  return response.data;
};

export const listMainlineDesignDocs = async () => {
  const response = await apiClient.get('/requirements/mainline-design-docs/');
  return response.data;
};

export const createMainlineDesignDoc = async (data: object) => {
  const response = await apiClient.post('/requirements/mainline-design-docs/', data);
  return response.data;
};

export const updateMainlineDesignDoc = async (docId: number, data: object) => {
  const response = await apiClient.put(`/requirements/mainline-design-docs/${docId}/`, data);
  return response.data;
};

export const deleteMainlineDesignDoc = async (docId: number) => {
  await apiClient.delete(`/requirements/mainline-design-docs/${docId}/`);
};

export const exportRequirementsToFeishu = (projectId: number, payload: Record<string, unknown>) =>
  apiClient.post(`/requirements/${projectId}/feishu/export/`, payload);

export const syncFeishuRequirements = (projectId: number, payload: Record<string, unknown>) =>
  apiClient.post(`/requirements/${projectId}/feishu/sync/`, payload);
